<script setup lang="ts">
interface MaskShape {
  id: string
  filename: string
  url: string
  width: number
  height: number
  svgContent?: string | null
}

interface Props {
  maskShape: MaskShape
  /** Any CSS colour; defaults to the page accent. */
  backgroundColor?: string | null
  image?: Media
  revealImage?: Media
  lazy?: boolean
  /** Extra classes for the image inside the shape — object-fit, mainly. */
  imageClass?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  backgroundColor: null,
  lazy: true,
})

const maskStyle = computed(() => useMaskStyle(props.maskShape))

const fillStyle = computed(() => ({ background: props.backgroundColor || MASK_ACCENT }))

// The fill is only a placeholder: it keeps the shape reading as solid colour
// while lazysizes swaps in a source. Once the image is there it would show
// through the letterboxing of a non-covering fit, so it goes. A non-lazy image
// never reports back -- there is nothing to wait for, so it starts hidden.
const imageLoaded = ref(!props.lazy)

// core-image is keyed on the image, so a swap remounts it and the new one
// reports in again -- the placeholder has to come back for that wait.
watch(() => props.image?.id, () => { imageLoaded.value = !props.lazy })

// With no image of its own the fill *is* the shape, so it always stays.
const showFill = computed(() => !props.image || !imageLoaded.value)
</script>

<template>
  <div class="group relative" :style="maskStyle">
    <div v-if="showFill" class="absolute inset-0" :style="fillStyle" />

    <core-image
      v-if="image"
      :key="image?.id"
      :image="image"
      :lazy="lazy"
      class="absolute inset-0 h-full object-cover"
      :class="imageClass"
      :apply-aspect-ratio-style="false"
      @loaded="imageLoaded = true"
    />
    <core-image
      v-if="revealImage"
      :image="revealImage"
      :lazy="lazy"
      class="absolute inset-0 h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity"
      :class="imageClass"
      :apply-aspect-ratio-style="false"
    />
  </div>
</template>
